import React, { useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";

import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import LockIcon from "@material-ui/icons/Lock";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";

import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === "dark";
	return {
	root: {
		minHeight: "100dvh",
		fallbacks: [{ minHeight: "100vh" }], // older WebViews without dvh
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		background: isDark ? "#0b0d0c" : "#f5f5f7",
		fontFamily: "'Roboto',system-ui,-apple-system,'Segoe UI','Helvetica Neue',sans-serif",
		padding: 24,
	},
	card: {
		background: isDark ? "#131614" : "#fff",
		borderRadius: 16,
		padding: 32,
		width: "100%",
		maxWidth: 420,
		border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
		boxShadow: "0 1px 2px rgba(15,15,15,0.04), 0 6px 18px rgba(15,15,15,0.04)",
	},
	brand: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		marginBottom: 24,
	},
	brandChip: {
		width: 36,
		height: 36,
		borderRadius: 10,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		background: "linear-gradient(135deg, #2ee37a 0%, #25d366 55%, #128c7e 100%)",
		color: "#fff",
		"& svg": { fontSize: 20 },
	},
	brandText: {
		fontWeight: 600,
		fontSize: 20,
		color: isDark ? "#f4f4f5" : "#0B0D0C",
		letterSpacing: -0.3,
	},
	title: {
		fontSize: 22,
		fontWeight: 600,
		margin: "0 0 8px",
		color: isDark ? "#f4f4f5" : "#0B0D0C",
	},
	subtitle: {
		fontSize: 14,
		color: isDark ? "rgba(255,255,255,0.6)" : "#71717a",
		margin: "0 0 24px",
		lineHeight: 1.5,
	},
	submitBtn: {
		marginTop: 16,
		padding: "12px 16px",
		background: "#0B0D0C",
		color: "#fff",
		fontWeight: 600,
		borderRadius: 999,
		textTransform: "none",
		"&:hover": { background: "#000" },
		"&:disabled": { background: "#a1a1aa", color: "#fff" },
	},
	errorBox: {
		background: "#fef2f2",
		border: "1px solid #fecaca",
		borderRadius: 10,
		padding: 14,
		marginTop: 16,
		fontSize: 14,
		color: "#991b1b",
		lineHeight: 1.5,
	},
	successBox: {
		background: "#ecfdf5",
		border: "1px solid #a7f3d0",
		borderRadius: 10,
		padding: 14,
		marginTop: 16,
		fontSize: 14,
		color: "#065f46",
		lineHeight: 1.5,
	},
	footerLine: {
		marginTop: 24,
		fontSize: 13,
		color: isDark ? "rgba(255,255,255,0.6)" : "#71717a",
		textAlign: "center",
	},
	footerLink: {
		color: isDark ? "#f4f4f5" : "#0B0D0C",
		fontWeight: 600,
	},
	};
});

// SECURITY: Token now arrives in window.location.hash (#token=<hex>)
// instead of the URL path. Fragments are not sent to the server,
// never logged in NGINX/CDN access logs, and not leaked in Referer
// headers. Reads happen synchronously via useState lazy init (guarded
// for SSR/test renders that have no window) so the correct branch
// (form vs invalid-link box) renders on the very first paint.
const TOKEN_RE = /^[a-f0-9]{64}$/i;

function readTokenFromHash() {
	if (typeof window === "undefined") return "";
	const raw = window.location.hash || "";
	if (!raw) return "";
	const params = new URLSearchParams(raw.replace(/^#/, ""));
	const t = params.get("token") || "";
	return TOKEN_RE.test(t) ? t : "";
}

const ResetPassword = () => {
	const classes = useStyles();
	const history = useHistory();
	const [token] = useState(() => readTokenFromHash());
	const tokenChecked = true;
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [showPw, setShowPw] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);
	const [error, setError] = useState("");

	const valid =
		password.length >= 8 &&
		password === confirm &&
		token && token.length === 64;

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!valid) return;
		setSubmitting(true);
		setError("");
		try {
			await openApi.post("/auth/password-reset/confirm", {
				token,
				newPassword: password,
			});
			setDone(true);
			// Redirect to login after 3 seconds
			setTimeout(() => history.push("/login"), 3000);
		} catch (err) {
			const msg = err?.response?.data?.error || "";
			if (msg.includes("ERR_PASSWORD_RESET_TOKEN_EXPIRED")) {
				setError(
					i18n.t("resetPassword.errors.expired", {
						defaultValue: "This reset link has expired. Request a new one from the forgot-password page.",
					})
				);
			} else if (msg.includes("ERR_PASSWORD_RESET_INVALID_TOKEN")) {
				setError(
					i18n.t("resetPassword.errors.invalid", {
						defaultValue: "This reset link is no longer valid. Request a new one.",
					})
				);
			} else if (msg.includes("ERR_PASSWORD_TOO_SHORT")) {
				setError(
					i18n.t("resetPassword.errors.short", {
						defaultValue: "Password must be at least 8 characters.",
					})
				);
			} else if (err?.response?.status === 429) {
				setError(
					i18n.t("resetPassword.errors.tooMany", {
						defaultValue: "Too many attempts. Please wait an hour and try again.",
					})
				);
			} else {
				setError(
					i18n.t("resetPassword.errors.generic", {
						defaultValue: "Something went wrong. Please try again.",
					})
				);
			}
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className={classes.root}>
			<div className={classes.card}>
				<div className={classes.brand}>
					<span className={classes.brandChip}><ChatRoundedIcon /></span>
					<span className={classes.brandText}>Whapiz</span>
				</div>

				<h1 className={classes.title}>
					{i18n.t("resetPassword.title", { defaultValue: "Set a new password" })}
				</h1>
				<p className={classes.subtitle}>
					{i18n.t("resetPassword.subtitle", {
						defaultValue: "Choose a strong password (at least 8 characters). You'll be redirected to sign in once it's saved.",
					})}
				</p>

				{tokenChecked && !token ? (
					<div className={classes.errorBox}>
						{i18n.t("resetPassword.errors.invalid", {
							defaultValue: "This reset link is no longer valid. Request a new one.",
						})}
						<div style={{ marginTop: 12 }}>
							<Link
								component={RouterLink}
								to="/forgot-password"
								style={{ color: "#0B0D0C", fontWeight: 600 }}
							>
								{i18n.t("resetPassword.requestNew", { defaultValue: "Request a new reset link" })}
							</Link>
						</div>
					</div>
				) : !done ? (
					<form onSubmit={handleSubmit} noValidate>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							required
							type={showPw ? "text" : "password"}
							autoComplete="new-password"
							autoFocus
							label={i18n.t("resetPassword.password", { defaultValue: "New password" })}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							disabled={submitting}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LockIcon style={{ color: "#a1a1aa" }} />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => setShowPw(!showPw)}
											tabIndex={-1}
										>
											{showPw ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							required
							type={showPw ? "text" : "password"}
							autoComplete="new-password"
							label={i18n.t("resetPassword.confirm", { defaultValue: "Confirm new password" })}
							value={confirm}
							onChange={(e) => setConfirm(e.target.value)}
							disabled={submitting}
							error={confirm.length > 0 && confirm !== password}
							helperText={
								confirm.length > 0 && confirm !== password
									? i18n.t("resetPassword.mismatch", { defaultValue: "Passwords don't match" })
									: ""
							}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LockIcon style={{ color: "#a1a1aa" }} />
									</InputAdornment>
								),
							}}
						/>
						{error ? <div className={classes.errorBox}>{error}</div> : null}
						<Button
							type="submit"
							fullWidth
							className={classes.submitBtn}
							disabled={submitting || !valid}
						>
							{submitting
								? i18n.t("resetPassword.saving", { defaultValue: "Saving…" })
								: i18n.t("resetPassword.submit", { defaultValue: "Save new password" })}
						</Button>
					</form>
				) : (
					<div className={classes.successBox}>
						{i18n.t("resetPassword.success", {
							defaultValue: "Password updated. Redirecting you to sign in…",
						})}
					</div>
				)}

				<div className={classes.footerLine}>
					<Link component={RouterLink} to="/login" className={classes.footerLink}>
						{i18n.t("resetPassword.backToLogin", { defaultValue: "← Back to login" })}
					</Link>
				</div>
			</div>
		</div>
	);
};

export default ResetPassword;
